import React, { useState, useEffect } from "react";
import "../styles/authLogin.css";
import Button from "react-bootstrap/Button";
import { useDispatch, useSelector } from "react-redux";
import { sendOtp, verifyOtp } from "../hook/authSlice";
import { Link, useNavigate } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import logo from "../../../assets/images/white-logo.png";

const AuthLoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { request_id } = useSelector(
    (state) => state.auth || {}
  );
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginType, setLoginType] = useState("");
  const [otp, setOtp] = useState("");
  const [otpShow, setOtpShow] = useState(false);
  const [timer, setTimer] = useState(30);
  const [sendingOtp, setSendingOtp] = useState("");
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [storedRequestId, setStoredRequestId] = useState("");
  /*
   * ==========================================
   * RESTORE LOGIN / OTP STATE AFTER REFRESH
   * ==========================================
   */
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    // Already logged in
    if (token) {
      navigate("/home", { replace: true });
      return;
    }

    const otpLogin = localStorage.getItem("otpLogin");
    const savedRequestId = localStorage.getItem("request_id");

    /*
     * OTP session exists
     * => Keep user on OTP screen after refresh
     */
    if (otpLogin && savedRequestId) {
      try {
        const otpData = JSON.parse(otpLogin);

        setLoginType(otpData.login_type || "");
        setStoredRequestId(savedRequestId);

        // IMPORTANT
        setOtpShow(true);

        // OTP should always be empty after refresh
        setOtp("");

        // Start timer
        setTimer(30);
      } catch (error) {
        console.error("Invalid OTP session:", error);

        localStorage.removeItem("otpLogin");
        localStorage.removeItem("request_id");

        setOtpShow(false);
        setStoredRequestId("");
      }
    } else {
      setOtpShow(false);
    }
  }, [navigate]);

  /*
   * ==========================================
   * OTP TIMER
   * ==========================================
   */
  useEffect(() => {
    if (!otpShow || timer <= 0) {
      return;
    }

    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [otpShow, timer]);

  /*
   * ==========================================
   * SEND OTP
   * ==========================================
   */
  const handleSendOtp = async (type) => {
    if (sendingOtp) return;

    if (!username.trim()) {
      toast.error("Enter Username");
      return;
    }

    if (!password.trim()) {
      toast.error("Enter Password");
      return;
    }

    setSendingOtp(type);

    try {
      const res = await dispatch(
        sendOtp({
          username: username.trim(),
          password,
          login_type: type,
        })
      ).unwrap();

      /*
       * Adjust these according to your API response.
       */
      const newRequestId =
        res?.request_id ||
        res?.data?.request_id ||
        res?.result?.request_id;

      if (!newRequestId) {
        console.error("OTP API response:", res);
        toast.error("Request ID not received from server");
        return;
      }

      /*
       * ======================================
       * SAVE OTP SESSION
       * ======================================
       *
       * Password intentionally NOT stored.
       */
      localStorage.setItem(
        "otpLogin",
        JSON.stringify({
          login_type: type,
          username: username.trim(),
        })
      );

      localStorage.setItem(
        "request_id",
        newRequestId
      );

      setStoredRequestId(newRequestId);
      setLoginType(type);

      setOtp("");
      setOtpShow(true);
      setTimer(30);

      toast.success("OTP Sent Successful.");
    } catch (error) {
      console.error("Send OTP Error:", error);

      toast.error(
        error?.message ||
          error?.error ||
          "Invalid Login"
      );
    } finally {
      setSendingOtp("");
    }
  };

  /*
   * ==========================================
   * VERIFY OTP
   * ==========================================
   */
  const handleVerifyOtp = async () => {
    if (!otp.trim() || otp.length !== 6) {
      toast.error("Enter valid 6 digit OTP");
      return;
    }

    /*
     * Redux request_id
     * OR
     * component request_id
     * OR
     * localStorage request_id
     */
    const finalRequestId =
      request_id ||
      storedRequestId ||
      localStorage.getItem("request_id");

    if (!finalRequestId) {
      toast.error("Request expired. Send OTP again");
      return;
    }

    setVerifyingOtp(true);

    try {
      await dispatch(
        verifyOtp({
          request_id: finalRequestId,
          otp: otp.trim(),
        })
      ).unwrap();

      /*
       * Login successful
       */
      toast.success("You have been logged in successfully.");
      /*
       * Remove OTP session
       */
      localStorage.removeItem("otpLogin");
      localStorage.removeItem("request_id");

      /*
       * Go to home
       */
      navigate("/home", {
        replace: true,
      });
    } catch (error) {
      console.error("Verify OTP Error:", error);

      toast.error(
        error?.message ||
          error?.error ||
          "Invalid OTP"
      );
    } finally {
      setVerifyingOtp(false);
    }
  };

  /*
   * ==========================================
   * ENTER KEY
   * ==========================================
   */
  const handleKeyDown = (e) => {
    if (e.key !== "Enter") return;

    if (!otpShow) {
      handleSendOtp(loginType || "sms");
    } else {
      handleVerifyOtp();
    }
  };

  /*
   * ==========================================
   * RESET OTP / LOGIN
   * ==========================================
   */
  const handleChangeNumber = () => {
    setUsername("");
    setPassword("");
    setLoginType("");

    setOtp("");
    setOtpShow(false);

    setTimer(30);
    setStoredRequestId("");

    localStorage.removeItem("otpLogin");
    localStorage.removeItem("request_id");
  };

  return (
    <>
      <Toaster position="top-right" />

      <section className="position-relative">
        <div className="bg-video-wrapper">
          <iframe
            src="https://youtube.com/embed/XCQS5NE2xFY?controls=0&rel=0&autoplay=1&loop=1&mute=1&playlist=XCQS5NE2xFY"
            title="YouTube video player"
            frameBorder="0"
            allow="autoplay; encrypted-media"
            allowFullScreen
          />
        </div>

        <div className="bg-overlay bg-linear-gradient-2"></div>

        <div className="container-fluid">
          <div className="row">
            <div className="col-12 p-0">
              <div className="d-flex flex-column min-vh-100 p-4">

                <div className="title-heading my-auto">

                  <div
                    className="card form-signin px-4 py-4 rounded-md shadow-sm"
                    style={{
                      background: "#00000094",
                      borderRadius: "12px",
                    }}
                  >
                    {/* LOGO */}
                    <div className="text-center">
                      <img
                        src={logo}
                        width="200"
                        alt="obsessions"
                        style={{
                          margin: "0 auto 20px",
                        }}
                      />
                    </div>

                    <div className="row">

                      {/* =================================
                          LOGIN SCREEN
                      ================================= */}
                      {!otpShow && (
                        <div className="col-lg-12">

                          {/* USERNAME */}
                          <div className="input-group">
                            <input
                              type="text"
                              id="name"
                              className="input-field"
                              placeholder=" "
                              value={username}
                              onChange={(e) =>
                                setUsername(e.target.value)
                              }
                              onKeyDown={handleKeyDown}
                            />

                            <label
                              htmlFor="name"
                              className="input-label"
                            >
                              Username
                            </label>
                          </div>

                          {/* PASSWORD */}
                          <div className="input-group">
                            <input
                              type="password"
                              id="password"
                              className="input-field"
                              placeholder=" "
                              value={password}
                              onChange={(e) =>
                                setPassword(e.target.value)
                              }
                              onKeyDown={handleKeyDown}
                            />

                            <label
                              htmlFor="password"
                              className="input-label"
                            >
                              Password
                            </label>
                          </div>

                          <div className="verify-divider">
                            <span>Choose Verification Method</span>
                          </div>

                          <div className="boxlogin">

                            {/* WHATSAPP */}
                            <button
                              type="button"
                              className="Button--secondary-whatsapp"
                              onClick={() =>
                                handleSendOtp("whatsapp")
                              }
                              disabled={
                                sendingOtp === "whatsapp"
                              }
                            >
                              <span className="Button-content">

                                <span className="Button-visual">
                                  {sendingOtp ===
                                  "whatsapp" ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                  ) : (
                                    <i className="bi bi-whatsapp"></i>
                                  )}
                                </span>

                                <span className="Button-label">
                                  {sendingOtp ===
                                  "whatsapp"
                                    ? "Sending..."
                                    : "Whatsapp"}
                                </span>

                              </span>
                            </button>

                            {/* SMS */}
                            <button
                              type="button"
                              className="Button--secondary-sms"
                              onClick={() =>
                                handleSendOtp("sms")
                              }
                              disabled={
                                sendingOtp === "sms"
                              }
                            >
                              <span className="Button-content">

                                <span className="Button-visual">
                                  {sendingOtp === "sms" ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                  ) : (
                                    <i className="bi bi-chat-square-text"></i>
                                  )}
                                </span>

                                <span className="Button-label">
                                  {sendingOtp === "sms"
                                    ? "Sending..."
                                    : "SMS"}
                                </span>

                              </span>
                            </button>

                            {/* EMAIL */}
                            {/* <button
                              type="button"
                              className="Button--secondary-email"
                              onClick={() =>
                                handleSendOtp("email")
                              }
                              disabled={
                                sendingOtp === "email"
                              }
                            >
                              <span className="Button-content">

                                <span className="Button-visual">
                                  {sendingOtp ===
                                  "email" ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                  ) : (
                                    <i className="bi bi-envelope"></i>
                                  )}
                                </span>

                                <span className="Button-label">
                                  {sendingOtp === "email"
                                    ? "Sending..."
                                    : "Email"}
                                </span>

                              </span>
                            </button> */}

                          </div>
                        </div>
                      )}

                      {/* =================================
                          OTP SCREEN
                      ================================= */}
                      {otpShow && (
                        <div className="col-lg-12">

                          {/* OTP INPUT */}
                          <div className="input-group">

                            <input
                              type="text"
                              id="otp"
                              className="input-field"
                              placeholder=" "
                              value={otp}
                              maxLength={6}
                              autoFocus
                              inputMode="numeric"
                              autoComplete="one-time-code"
                              onChange={(e) =>
                                setOtp(
                                  e.target.value
                                    .replace(/\D/g, "")
                                    .slice(0, 6)
                                )
                              }
                              onKeyDown={handleKeyDown}
                            />

                            <label
                              htmlFor="otp"
                              className="input-label"
                            >
                              Enter 6 digit valid OTP
                            </label>

                          </div>

                          {/* BACK + TIMER */}
                          <div className="d-flex justify-content-between mb-2">

                            <Link
                              to="#"
                              onClick={(e) => {
                                e.preventDefault();
                                handleChangeNumber();
                              }}
                              style={{
                                color: "#FFF",
                                textDecoration: "none",
                              }}
                            >
                              Back / Reset?
                            </Link>

                            <div
                              style={{
                                color: "#FFF",
                              }}
                             >

                              {timer > 0 ? (
                                <span>
                                  00:
                                  {String(timer).padStart(
                                    2,
                                    "0"
                                  )}
                                </span>
                              ) : (
                                <span
                                  style={{
                                    cursor: "pointer",
                                  }}
                                  onClick={() => {
                                    setTimer(30);
                                    handleSendOtp(
                                      loginType
                                    );
                                  }}
                                >
                                    Resend OTP
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* VERIFY BUTTON */}
                      <div className="col-lg-12">

                        {otpShow && (
                          <Button
                            type="button"
                            className="btn btn-success login rounded-md w-100"
                            onClick={handleVerifyOtp}
                            disabled={verifyingOtp}
                          >

                            {verifyingOtp ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                ></span>

                                Verifying...
                              </>
                            ) : (
                              "Verify OTP"
                            )}

                          </Button>
                        )}

                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AuthLoginPage;